import { CloseOutlined } from '@ant-design/icons';
import { LoaderSpin } from '@framework/ui/components/loader/LoaderSpin.tsx';
import { Button, Collapse, Typography } from 'antd';
import clx from 'classnames';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { keyframes } from 'styled-components';
import { type IIdentifyLayerResult } from '../drawing/identify/IIdentifyResult.ts';
import { MapIdentifyLayerResults } from './MapIdentifyLayerResults.tsx';

export interface IMapIdentifyResultsPanelProps {
  /** `null` = nessuna interrogazione eseguita, `[]` = interrogazione senza risultati. */
  results: IIdentifyLayerResult[] | null;
  isLoading: boolean;
  onClose: () => void;
  /**
   * Se true il pannello è un overlay flottante (vista estesa): larghezza
   * fissa, altezza massima con scroll interno e ombra marcata. In inline
   * (false) è una card a piena larghezza sotto la mappa.
   */
  floating?: boolean;
  isMobile?: boolean | null;
}

const enter = keyframes`
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Body = styled.div``;

const PanelCard = styled.div`
  background: ${({ theme }) => theme.colorBgElevated ?? theme.colorBgContainer};
  border: 1px solid ${({ theme }) => theme.colorBorderSecondary};
  border-radius: ${({ theme }) => theme.borderRadius}px;
  overflow: hidden;
  animation: ${enter} 0.25s ease;

  /* Inline: card a piena larghezza sotto la mappa. */
  &:not(.floating) {
    margin-top: 1rem;
    box-shadow: ${({ theme }) => theme.boxShadowTertiary};
  }

  /* Vista estesa: overlay flottante, larghezza fissa, scroll interno. */
  &.floating {
    width: 380px;
    max-width: 100%;
    max-height: calc(100vh - 200px);
    display: flex;
    flex-direction: column;
    box-shadow: ${({ theme }) => theme.boxShadowSecondary ?? theme.boxShadow};

    /* Mobile: il pannello flottante occupa tutta la larghezza. */
    &.mobile {
      width: 100%;
    }

    /* Il corpo scrolla internamente solo quando il pannello è flottante. */
    ${Body} {
      flex: 1;
      min-height: 0;
      overflow-y: auto;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const HeaderBand = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 4px 4px 4px 12px;
  background: ${({ theme }) => theme.colorPrimary};
`;

const HeaderTitle = styled.span`
  font-size: 14px;
  font-weight: 600;
  line-height: 1.4;
  color: #fff;
`;

const CloseButton = styled(Button)`
  &&& {
    color: #fff;

    &:hover,
    &:focus-visible {
      color: #fff;
      background: rgba(255, 255, 255, 0.2);
    }
  }
`;

/** Contenitore per stati loading/vuoto: garantisce un'altezza minima così che
 * la rotellina di caricamento sia sempre visibile. */
const StateBox = styled.div`
  min-height: 96px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px 12px;
  text-align: center;
`;

/** Accordion dei layer interrogati: header neutro, tabella a filo (no padding). */
const ResultsCollapse = styled(Collapse)`
  && {
    border: 0;
    border-radius: 0;
    background: transparent;

    > .ant-collapse-item {
      border-bottom: 1px solid ${({ theme }) => theme.colorBorderSecondary};

      &:last-child {
        border-bottom: 0;
      }

      /* Header del layer: fill secondario (più marcato del grigio dell'header
         di tabella, così i due non si confondono). */
      > .ant-collapse-header {
        align-items: center;
        padding: 8px 12px;
        background: ${({ theme }) => theme.colorFillSecondary};
        color: ${({ theme }) => theme.colorText};
        font-size: 13px;
        font-weight: 600;
      }

      > .ant-collapse-content {
        border-top: 0;

        > .ant-collapse-content-box {
          padding: 0;
        }
      }
    }
  }
`;

/**
 * Pannello con i risultati dell'interrogazione (identify), organizzato per
 * layer in un accordion. Usato sia inline (card sotto la mappa) sia in vista
 * estesa (overlay flottante, `floating`). All'arrivo di nuovi risultati anima
 * l'ingresso e, inline, si porta in vista con uno scroll fluido.
 */
export function MapIdentifyResultsPanel(props: IMapIdentifyResultsPanelProps) {
  const { results, isLoading, onClose, floating, isMobile } = props;
  const { t } = useTranslation();

  // Chiave di rendering che cambia a ogni nuova interrogazione: forza il
  // rimontaggio della card così l'animazione di ingresso viene rieseguita.
  const renderKeyRef = useRef(0);
  const prevResultsRef = useRef<typeof results>(undefined);
  if (prevResultsRef.current !== results) {
    prevResultsRef.current = results;
    renderKeyRef.current += 1;
  }

  const cardRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // Inline: porta la card sotto la mappa in vista, altrimenti l'utente
    // potrebbe non accorgersi che sono arrivati dei risultati.
    if (!floating && results != null) {
      cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [floating, results]);

  if (!results && !isLoading) return null;

  const items = (results ?? []).map((result, index) => ({
    key: String(index),
    label: `${result.layerTitle} (${t('mappa.identify.risultati-trovati', {
      count: result.features.length,
      defaultValue: 'Trovati {{count}} risultati',
    })})`,
    children: (
      <MapIdentifyLayerResults result={result} aliases={result.aliases} />
    ),
  }));

  return (
    <PanelCard
      key={renderKeyRef.current}
      ref={cardRef}
      className={clx({ floating, mobile: isMobile })}
    >
      <HeaderBand>
        <HeaderTitle>
          {t('mappa.identify.risultati', {
            defaultValue: 'Risultati interrogazione',
          })}
        </HeaderTitle>
        <CloseButton
          type="text"
          aria-label={t('mappa.livelli.popover.pulsante-chiudi', 'Chiudi')}
          icon={<CloseOutlined aria-hidden />}
          onClick={onClose}
        />
      </HeaderBand>
      <Body>
        {isLoading ? (
          <StateBox>
            <LoaderSpin transparent noPadding />
          </StateBox>
        ) : items.length === 0 ? (
          <StateBox>
            <Typography.Text type="secondary">
              {t('mappa.identify.nessun-risultato', {
                defaultValue: 'Nessun risultato trovato',
              })}
            </Typography.Text>
          </StateBox>
        ) : (
          <ResultsCollapse
            defaultActiveKey={items.map(item => item.key)}
            items={items}
          />
        )}
      </Body>
    </PanelCard>
  );
}
